import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';

/**
 * ProspectManager - Manage M2M prospects for leads/opportunities
 * Handles CRUD operations, primary marking, and role assignment
 */
const ProspectManager = ({
  entity,
  entityType,
  isCreateMode = false,
  onChange = null, // Callback for create mode to pass prospect data up
  onError = null // Callback for error messages
}) => {
  const [attachedProspects, setAttachedProspects] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [prospectTypes, setProspectTypes] = useState([]);

  // New prospect form data
  const [newProspectData, setNewProspectData] = useState({
    full_name: '', first_name: '', last_name: '', email: '',
    title: '', prospect_type: '', role: 'Contact', is_primary: false
  });

  // Load attached prospects in edit mode
  useEffect(() => {
    if (!isCreateMode && entity?.prospects) {
      // Map prospects with relation metadata if available
      const prospects = entity.prospects.map((prospect, index) => ({
        ...prospect,
        relation: {
          is_primary: prospect.is_primary || (index === 0), // First one is primary by default
          role: prospect.role || 'Contact',
          can_remove: true
        }
      }));
      setAttachedProspects(prospects);
    } else if (!isCreateMode && !entity?.prospects) {
      // Reset when entity has no prospects
      setAttachedProspects([]);
    }
  }, [entity?.id, entity?.prospects, isCreateMode]);

  // Fetch prospect types on mount
  useEffect(() => {
    const fetchProspectTypes = async () => {
      try {
        const response = await adminApi.get('/admin/sales/settings/prospect-types/');
        const types = response.data?.results || response.data || [];
        setProspectTypes(types);
        // Set default to 'individual' if available
        const individualType = types.find(t => t.slug === 'individual');
        if (individualType) {
          setNewProspectData(prev => ({ ...prev, prospect_type: individualType.id }));
        }
      } catch (error) {
        console.error('Error fetching prospect types:', error);
      }
    };
    fetchProspectTypes();
  }, []);

  // Search for existing prospects
  const handleSearch = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await adminApi.get('/admin/sales/prospects/', {
        params: { search: query, limit: 10 }
      });
      const results = response.data?.results || response.data || [];
      setSearchResults(results);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error searching prospects:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSearch(searchQuery);
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Attach existing prospect
  const handleAttachProspect = async (prospect) => {
    if (isCreateMode) {
      // In create mode, just add to local state
      const newProspect = {
        ...prospect,
        relation: {
          is_primary: attachedProspects.length === 0,
          role: 'Contact',
          can_remove: true
        }
      };
      setAttachedProspects([...attachedProspects, newProspect]);
      setSearchQuery('');
      setShowSearchResults(false);

      // Notify parent
      if (onChange) {
        onChange([...attachedProspects, newProspect]);
      }
    } else {
      // In edit mode, make API call to attach
      try {
        const endpoint = entityType === 'lead'
          ? `/admin/sales/leads/${entity.id}/prospects/`
          : `/admin/sales/opportunities/${entity.id}/prospects/`;

        await adminApi.post(endpoint, {
          prospect_id: prospect.id,
          is_primary: attachedProspects.length === 0,
          role: 'Contact'
        });

        // Reload entity or update local state
        const newProspect = {
          ...prospect,
          relation: {
            is_primary: attachedProspects.length === 0,
            role: 'Contact',
            can_remove: true
          }
        };
        setAttachedProspects([...attachedProspects, newProspect]);
        setSearchQuery('');
        setShowSearchResults(false);
      } catch (error) {
        alert('Failed to attach prospect. Please try again.');
      }
    }
  };

  // Create new prospect and attach
  const handleCreateProspect = async (e) => {
    e.preventDefault();

    if (!newProspectData.full_name && !newProspectData.email) {
      alert('Please provide at least a name or email');
      return;
    }

    if (isCreateMode) {
      // In create mode, just add to local state
      const prospect = {
        id: `temp-${Date.now()}`,
        full_name: newProspectData.full_name,
        first_name: newProspectData.first_name,
        last_name: newProspectData.last_name,
        email: newProspectData.email,
        title: newProspectData.title,
        relation: {
          is_primary: newProspectData.is_primary || attachedProspects.length === 0,
          role: newProspectData.role || 'Contact',
          can_remove: true
        },
        _isNew: true // Flag to indicate this needs to be created
      };

      setAttachedProspects([...attachedProspects, prospect]);
      setShowCreateForm(false);

      // Reset form with default prospect type
      const individualType = prospectTypes.find(t => t.slug === 'individual');
      setNewProspectData({
        full_name: '',
        first_name: '',
        last_name: '',
        email: '',
        title: '',
        prospect_type: individualType?.id || '',
        role: 'Contact',
        is_primary: false
      });

      // Notify parent
      if (onChange) {
        onChange([...attachedProspects, prospect]);
      }
    } else {
      // In edit mode, create prospect then attach
      try {
        // First create the prospect
        const createResponse = await adminApi.post('/admin/sales/prospects/', {
          full_name: newProspectData.full_name,
          first_name: newProspectData.first_name,
          last_name: newProspectData.last_name,
          email: newProspectData.email,
          title: newProspectData.title,
          sales_organization: entity.sales_organization?.id || entity.sales_organization,
          prospect_type: newProspectData.prospect_type
        });

        const newProspect = createResponse.data;

        // Then attach it
        const endpoint = entityType === 'lead'
          ? `/admin/sales/leads/${entity.id}/prospects/`
          : `/admin/sales/opportunities/${entity.id}/prospects/`;

        await adminApi.post(endpoint, {
          prospect_id: newProspect.id,
          is_primary: newProspectData.is_primary || attachedProspects.length === 0,
          role: newProspectData.role || 'Contact'
        });

        // Add to local state
        const prospect = {
          ...newProspect,
          relation: {
            is_primary: newProspectData.is_primary || attachedProspects.length === 0,
            role: newProspectData.role || 'Contact',
            can_remove: true
          }
        };
        setAttachedProspects([...attachedProspects, prospect]);
        setShowCreateForm(false);

        // Reset form with default prospect type
        const individualType = prospectTypes.find(t => t.slug === 'individual');
        setNewProspectData({
          full_name: '',
          first_name: '',
          last_name: '',
          email: '',
          title: '',
          prospect_type: individualType?.id || '',
          role: 'Contact',
          is_primary: false
        });
      } catch (error) {
        console.error('Error creating prospect:', error);
        alert('Failed to create prospect. Please try again.');
      }
    }
  };

  // Remove prospect
  const handleRemoveProspect = async (prospect) => {
    if (isCreateMode) {
      // Just remove from local state
      const updated = attachedProspects.filter(p => p.id !== prospect.id);
      setAttachedProspects(updated);

      if (onChange) {
        onChange(updated);
      }
    } else {
      // Make API call to detach
      if (!confirm(`Remove ${prospect.full_name || 'this prospect'}?`)) {
        return;
      }

      try {
        const endpoint = entityType === 'lead'
          ? `/admin/sales/leads/${entity.id}/prospects/${prospect.id}/`
          : `/admin/sales/opportunities/${entity.id}/prospects/${prospect.id}/`;

        await adminApi.delete(endpoint);

        const updated = attachedProspects.filter(p => p.id !== prospect.id);
        setAttachedProspects(updated);
      } catch (error) {
        console.error('Error removing prospect:', error);
        const errorMsg = error.response?.data?.message ||
                        error.response?.data?.error ||
                        error.response?.data?.detail ||
                        (error.response?.status === 403 ? 'Permission denied. You do not have access to delete this prospect.' :
                        'Failed to remove prospect. Please try again.');
        if (onError) {
          onError(errorMsg);
        } else {
          alert(errorMsg);
        }
      }
    }
  };

  // Mark as primary
  const handleMarkPrimary = async (prospect) => {
    if (isCreateMode) {
      // Update local state
      const updated = attachedProspects.map(p => ({
        ...p,
        relation: {
          ...p.relation,
          is_primary: p.id === prospect.id
        }
      }));
      setAttachedProspects(updated);

      if (onChange) {
        onChange(updated);
      }
    } else {
      // Make API call
      try {
        const endpoint = entityType === 'lead'
          ? `/admin/sales/leads/${entity.id}/prospects/${prospect.id}/`
          : `/admin/sales/opportunities/${entity.id}/prospects/${prospect.id}/`;

        await adminApi.patch(endpoint, { is_primary: true });

        const updated = attachedProspects.map(p => ({
          ...p,
          relation: {
            ...p.relation,
            is_primary: p.id === prospect.id
          }
        }));
        setAttachedProspects(updated);
      } catch (error) {
        console.error('Error updating prospect:', error);
        alert('Failed to update prospect. Please try again.');
      }
    }
  };

  // Update role
  const handleUpdateRole = async (prospect, newRole) => {
    if (isCreateMode) {
      const updated = attachedProspects.map(p =>
        p.id === prospect.id
          ? { ...p, relation: { ...p.relation, role: newRole } }
          : p
      );
      setAttachedProspects(updated);

      if (onChange) {
        onChange(updated);
      }
    } else {
      try {
        const endpoint = entityType === 'lead'
          ? `/admin/sales/leads/${entity.id}/prospects/${prospect.id}/`
          : `/admin/sales/opportunities/${entity.id}/prospects/${prospect.id}/`;

        await adminApi.patch(endpoint, { role: newRole });

        const updated = attachedProspects.map(p =>
          p.id === prospect.id
            ? { ...p, relation: { ...p.relation, role: newRole } }
            : p
        );
        setAttachedProspects(updated);
      } catch (error) {
        console.error('Error updating role:', error);
      }
    }
  };

  const roleOptions = [
    'Contact',
    'Decision Maker',
    'Influencer',
    'Champion',
    'Gatekeeper',
    'Technical Evaluator',
    'Economic Buyer',
    'End User'
  ];

  return (
    <div className="prospect-manager">
      {/* Attached Prospects List */}
      {attachedProspects.length > 0 && (
        <div className="mb-3">
          {/* <label className="form-label form-label-sm mb-2 fw-bold">
            <i className="fas fa-users me-1"></i>
            Associated Prospects ({attachedProspects.length})
          </label> */}
          <div className="list-group list-group-sm">
            {attachedProspects.map((prospect) => (
              <div key={prospect.id} className="list-group-item p-2">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center gap-2">
                      <div className="fw-bold text-primary">
                        {prospect.full_name || `${prospect.first_name || ''} ${prospect.last_name || ''}`.trim() || 'Unnamed Prospect'}
                      </div>
                      {prospect.relation?.is_primary && (
                        <span className="badge bg-success" style={{ fontSize: '0.7rem' }}>
                          <i className="fas fa-star me-1"></i>Primary
                        </span>
                      )}
                    </div>

                    {prospect.title && (
                      <div className="text-muted small">
                        <i className="fas fa-briefcase me-1"></i>
                        {prospect.title}
                      </div>
                    )}

                    <div className="d-flex flex-wrap gap-2 mt-1 align-items-center">
                      {prospect.email && (
                        <span className="badge bg-light text-dark" style={{ fontSize: '0.7rem' }}>
                          <i className="fas fa-envelope me-1"></i>
                          {prospect.email}
                        </span>
                      )}

                      {/* Role Selector */}
                      <div className="d-flex align-items-center gap-1">
                        <span className="text-muted" style={{ fontSize: '0.7rem' }}>Role:</span>
                        <select
                          className="form-select form-select-sm"
                          style={{ fontSize: '0.7rem', width: 'auto', padding: '2px 6px' }}
                          value={prospect.relation?.role || 'Contact'}
                          onChange={(e) => handleUpdateRole(prospect, e.target.value)}
                        >
                          {roleOptions.map(role => (
                            <option key={role} value={role}>{role}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="d-flex gap-1">
                    {!prospect.relation?.is_primary && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-success"
                        style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                        onClick={() => handleMarkPrimary(prospect)}
                        title="Mark as primary contact"
                      >
                        <i className="fas fa-star"></i>
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                      onClick={() => handleRemoveProspect(prospect)}
                      title="Remove prospect"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search & Add Section */}
      <div className="mb-2">
        <div className="d-flex gap-2 mb-2">
          {!showCreateForm && (<button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => {
              setShowCreateForm(!showCreateForm);
              setShowSearchResults(false);
            }}
          >
            {attachedProspects.length > 0 ? ('Add Another'):('Add')} Prospect
          </button>)}
          <div className="flex-grow-1 position-relative">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search existing prospects by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => searchQuery && setShowSearchResults(true)}
            />
            {isSearching && (
              <div className="position-absolute end-0 top-50 translate-middle-y me-2">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
            )}

            {/* Search Results Dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div
                className="position-absolute w-100 mt-1 shadow-sm border rounded bg-white"
                style={{ maxHeight: '250px', overflowY: 'auto', zIndex: 1000 }}
              >
                {searchResults.map(prospect => {
                  const isAttached = attachedProspects.some(p => p.id === prospect.id);
                  return (
                    <div
                      key={prospect.id}
                      className={`p-2 ${isAttached ? 'bg-light text-muted' : 'cursor-pointer'}`}
                      style={{
                        borderBottom: '1px solid #eee',
                        cursor: isAttached ? 'not-allowed' : 'pointer'
                      }}
                      onClick={() => !isAttached && handleAttachProspect(prospect)}
                    >
                      <div className="fw-bold">{prospect.full_name}</div>
                      {prospect.email && (
                        <div className="small text-muted">{prospect.email}</div>
                      )}
                      {prospect.title && (
                        <div className="small text-muted">{prospect.title}</div>
                      )}
                      {isAttached && (
                        <span className="badge bg-secondary small">Already attached</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create New Prospect Form */}
      {showCreateForm && (
        <div className="card mb-2">
          <div className="card-header py-2 bg-light">
            <h6 className="mb-0">Create New Prospect</h6>
          </div>
          <div className="card-body p-2">
            <form onSubmit={handleCreateProspect}>
              <div className="row g-2 mb-2">
                <div className="col-md-6">
                  <label className="form-label form-label-sm mb-1">Full Name *</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={newProspectData.full_name}
                    onChange={(e) => setNewProspectData({ ...newProspectData, full_name: e.target.value })}
                    placeholder="John Doe"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label form-label-sm mb-1">Email *</label>
                  <input
                    type="email"
                    className="form-control form-control-sm"
                    value={newProspectData.email}
                    onChange={(e) => setNewProspectData({ ...newProspectData, email: e.target.value })}
                    placeholder="john.doe@company.com"
                  />
                </div>
              </div>

              <div className="row g-2 mb-2">
                <div className="col-md-6">
                  <label className="form-label form-label-sm mb-1">Job Title</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={newProspectData.title}
                    onChange={(e) => setNewProspectData({ ...newProspectData, title: e.target.value })}
                    placeholder="Director of Sales"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label form-label-sm mb-1">Prospect Type</label>
                  <select
                    className="form-select form-select-sm"
                    value={newProspectData.prospect_type}
                    onChange={(e) => setNewProspectData({ ...newProspectData, prospect_type: e.target.value })}
                  >
                    <option value="">Select type...</option>
                    {prospectTypes.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="row g-2 mb-2">
                <div className="col-md-12">
                  <label className="form-label form-label-sm mb-1">Role</label>
                  <select
                    className="form-select form-select-sm"
                    value={newProspectData.role}
                    onChange={(e) => setNewProspectData({ ...newProspectData, role: e.target.value })}
                  >
                    {roleOptions.map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-check mb-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="new_prospect_primary"
                  checked={newProspectData.is_primary}
                  onChange={(e) => setNewProspectData({ ...newProspectData, is_primary: e.target.checked })}
                />
                <label className="form-check-label" htmlFor="new_prospect_primary">
                  Mark as primary contact
                </label>
              </div>

              <div className="d-flex gap-2">
                <button type="button" className="btn btn-sm btn-primary" onClick={handleCreateProspect} >
                  Create & Attach
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => setShowCreateForm(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProspectManager;
